// Remembers how tall each link's preview card rendered (0 = no card) so a
// revisit reserves the exact space before the preview arrives. Keys are
// hashes, so no URL list from encrypted rooms is stored in plain text.

const STORAGE_KEY = "chat.linkPreviewHeights.v1";
const CAP = 500;

let heights: Map<string, number> | null = null;

function load(): Map<string, number> {
  if (heights) return heights;
  heights = new Map();
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        if (
          Array.isArray(entry) &&
          typeof entry[0] === "string" &&
          typeof entry[1] === "number" &&
          entry[1] >= 0
        ) {
          heights.set(entry[0], entry[1]);
        }
      }
    }
  } catch {
    // Unavailable or corrupt storage just means no remembered layout.
  }
  return heights;
}

/** FNV-1a over the URL; stable across launches, not reversible to a list of links. */
export function linkKey(url: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < url.length; i++) {
    hash ^= url.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(36)}.${url.length.toString(36)}`;
}

export function rememberedPreviewHeight(url: string): number | undefined {
  return load().get(linkKey(url));
}

export function rememberPreviewHeight(url: string, height: number): void {
  const map = load();
  const key = linkKey(url);
  const rounded = Math.max(0, Math.round(height));
  if (map.get(key) === rounded) return;
  map.delete(key);
  map.set(key, rounded);
  while (map.size > CAP) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify([...map]));
  } catch {
    // Quota or private mode: the in-memory map still serves this session.
  }
}

/** Test seam: forget the in-memory copy so the next read reloads storage. */
export function resetPreviewHeightsForTest(): void {
  heights = null;
}
